import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { BsArrowLeft, BsGithub } from "react-icons/bs";
import { CgWebsite } from "react-icons/cg";
import Particle from "../Particle";
import projectData, {
  getProjectFocusLabel,
  normalizeProjectFocus,
} from "../../data/projectData";
import usePageMeta from "../../hooks/usePageMeta";

function ProjectDetail() {
  const { slug } = useParams();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const project = projectData.find((item) => item.slug === slug);
  const rawFocusParam = new URLSearchParams(search).get("focus")?.trim() || "";
  const focusParam = normalizeProjectFocus(rawFocusParam);
  const normalizedSearchParams = new URLSearchParams(search);

  if (rawFocusParam && focusParam && focusParam !== rawFocusParam) {
    normalizedSearchParams.set("focus", focusParam);
  }

  const normalizedSearch = normalizedSearchParams.toString();

  usePageMeta(
    project ? `${project.title} | Om Jaiswal` : "Project | Om Jaiswal",
    project
      ? `${project.description} Case study by Om Jaiswal covering ${project.stack.join(", ")}.`
      : "Selected project detail by Om Jaiswal."
  );

  React.useEffect(() => {
    if (!rawFocusParam || !focusParam || rawFocusParam === focusParam) {
      return;
    }

    navigate(
      {
        pathname,
        search: `?${normalizedSearch}`,
      },
      { replace: true }
    );
  }, [focusParam, navigate, normalizedSearch, pathname, rawFocusParam]);

  if (!project) {
    return <Navigate to="/projects" replace />;
  }

  const activeFocusAreas = project.focusAreas || [];
  const relatedProjects = projectData
    .filter((item) => item.slug !== project.slug)
    .map((item) => ({
      ...item,
      relatedScore: (item.focusAreas || []).filter((focusArea) =>
        activeFocusAreas.includes(focusArea)
      ).length,
    }))
    .sort((leftProject, rightProject) => {
      if (rightProject.relatedScore !== leftProject.relatedScore) {
        return rightProject.relatedScore - leftProject.relatedScore;
      }

      if (Number(rightProject.featured) !== Number(leftProject.featured)) {
        return Number(rightProject.featured) - Number(leftProject.featured);
      }

      return leftProject.title.localeCompare(rightProject.title);
    })
    .slice(0, 3);
  const focusLabels = project.focusAreas?.map(getProjectFocusLabel) || [];
  const browseBackTarget = normalizedSearch
    ? {
        pathname: "/projects",
        search: `?${normalizedSearch}`,
      }
    : "/projects";
  const browseBackLabel = focusParam
    ? "Back to matching projects"
    : "Back to selected projects";
  const briefSearchParams = new URLSearchParams();

  if (project.inquiryType) {
    briefSearchParams.set("projectType", project.inquiryType);
  }

  if (focusParam) {
    briefSearchParams.set("focus", focusParam);
  }

  briefSearchParams.set("sourceProject", project.title);

  const briefLink = {
    pathname: "/",
    search: `?${briefSearchParams.toString()}`,
    hash: "#project-brief",
  };
  const summaryCards = [
    {
      label: "Project type",
      value: project.featuredLabel,
    },
    {
      label: "Interface focus",
      value: project.stack.join(" · "),
    },
    {
      label: "Strongest signal",
      value: project.highlights?.[0],
    },
    {
      label: "Best fit",
      value: project.clientFit,
    },
  ].filter((item) => item.value);

  return (
    <Container fluid className="project-section project-detail-section">
      <Particle />
      <Container className="project-detail-shell section-shell">
        <div className="project-detail-breadcrumb">
          <Link to={browseBackTarget} className="project-detail-back-link">
            <BsArrowLeft />
            <span>{browseBackLabel}</span>
          </Link>
        </div>

        <section className="project-detail-hero">
          <Row className="align-items-start g-4 project-detail-hero-row">
            <Col lg={7}>
              <span className="project-card-label">{project.featuredLabel}</span>
              <h1 className="section-title project-detail-title">{project.title}</h1>
              <p className="section-copy project-detail-copy">
                {project.description}
              </p>

              {project.stack?.length > 0 && (
                <div className="project-card-stack project-detail-stack">
                  {project.stack.map((item) => (
                    <span key={item} className="skill-pill">
                      {item}
                    </span>
                  ))}
                </div>
              )}

              {focusLabels.length > 0 && (
                <div className="project-focus-block project-detail-focus">
                  <span className="project-mini-heading">Relevant for</span>
                  <div className="project-focus-list">
                    {focusLabels.map((label) => (
                      <span key={label} className="project-focus-pill">
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {project.ownership?.length > 0 && (
                <div className="project-detail-ownership">
                  <span className="project-mini-heading">What I handled</span>
                  <div className="project-ownership-list">
                    {project.ownership.map((item) => (
                      <span key={item} className="project-ownership-pill">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="project-detail-actions">
                <Button
                  as={Link}
                  to={briefLink}
                  className="project-action-button"
                >
                  Start a similar project
                </Button>
                <Button
                  as="a"
                  href={project.ghLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outline-light"
                  className="project-action-button project-secondary-btn"
                  aria-label={`Open source reference for ${project.title} in a new tab`}
                >
                  <BsGithub />
                  <span>Source reference</span>
                </Button>
                {project.demoLink ? (
                  <Button
                    as="a"
                    href={project.demoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outline-light"
                    className="project-action-button project-secondary-btn"
                    aria-label={`Open ${project.title} ${project.demoLabel || "walkthrough"} in a new tab`}
                  >
                    <CgWebsite />
                    <span>{project.demoLabel || "Walkthrough"}</span>
                  </Button>
                ) : null}
              </div>
            </Col>

            <Col lg={5}>
              <div className="project-detail-side-stack">
                <div className="project-detail-media-wrap">
                  <img
                    src={project.imgPath}
                    alt={`${project.title} preview`}
                    className="project-detail-image"
                  />
                </div>

                <aside className="project-detail-summary-card project-detail-snapshot-card">
                  <span className="project-mini-heading">Project snapshot</span>
                  <div className="project-detail-snapshot-list">
                    {summaryCards.map((item) => (
                      <article key={item.label} className="project-detail-snapshot-item">
                        <span className="project-detail-snapshot-label">{item.label}</span>
                        <p>{item.value}</p>
                      </article>
                    ))}
                  </div>
                </aside>
              </div>
            </Col>
          </Row>
        </section>

        {project.caseStudy ? (
          <section className="project-detail-case-study">
            <div className="section-header section-header-left">
              <p className="section-kicker">Case study</p>
              <h2 className="section-title section-title-compact">
                The workflow story behind the build.
              </h2>
              <p className="section-supporting-copy section-supporting-copy-left">
                This is the part a client should evaluate closely: the problem,
                the interface decisions, and why the implementation matters
                beyond a surface-level demo.
              </p>
            </div>

            <div className="project-case-study-grid project-detail-case-study-grid">
              <article className="project-case-study-card">
                <span className="project-mini-heading">Problem</span>
                <p>{project.caseStudy.problem}</p>
              </article>
              <article className="project-case-study-card">
                <span className="project-mini-heading">What I built</span>
                <p>{project.caseStudy.delivery}</p>
              </article>
              <article className="project-case-study-card">
                <span className="project-mini-heading">Why it matters</span>
                <p>{project.caseStudy.outcome}</p>
              </article>
            </div>
          </section>
        ) : null}

        <Row className="g-4 project-detail-lower-grid">
          <Col lg={7}>
            <section className="project-detail-panel">
              <p className="section-kicker">Signals to notice</p>
              <h2 className="project-detail-panel-title">
                What this project proves.
              </h2>
              <ul className="project-highlight-list project-detail-highlight-list">
                {project.highlights?.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          </Col>

          <Col lg={5}>
            <section className="project-detail-panel project-detail-evaluation-panel">
              <p className="section-kicker">Client lens</p>
              <h2 className="project-detail-panel-title">
                How to evaluate similar work.
              </h2>
              <ul className="resume-side-list">
                <li>Judge whether the workflow becomes simpler for the person using it every day.</li>
                <li>Look for interface structure that keeps the product feeling dependable, not fragile.</li>
                <li>Check whether integrations support the experience instead of distracting from it.</li>
              </ul>
            </section>
          </Col>
        </Row>

        {relatedProjects.length > 0 ? (
          <section className="project-detail-related-section">
            <div className="section-header section-header-left">
              <p className="section-kicker">Related work</p>
              <h2 className="project-detail-panel-title">More selected projects.</h2>
            </div>

            <div className="project-detail-related-grid">
              {relatedProjects.map((item) => (
                <article key={item.slug} className="project-detail-related-card">
                  <span className="project-card-label">{item.featuredLabel}</span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <Button
                    as={Link}
                    to={{
                      pathname: `/projects/${item.slug}`,
                      search: normalizedSearch ? `?${normalizedSearch}` : "",
                    }}
                    variant="outline-light"
                  >
                    {item.caseStudy ? "Read case study" : "View project detail"}
                  </Button>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        <div className="projects-cta-banner project-detail-cta-banner">
          <div>
            <p className="section-kicker">Need similar help?</p>
            <h2>Start with the workflow, the constraint, and the goal.</h2>
            <p>
              If you are evaluating a related build, send the project brief and
              I will respond with the clearest next step.
            </p>
          </div>

          <div className="projects-cta-actions">
            <Button as={Link} to={briefLink}>
              Send a structured brief
            </Button>
            <Button as={Link} to="/experience" variant="outline-light">
              Review Experience
            </Button>
          </div>
        </div>
      </Container>
    </Container>
  );
}

export default ProjectDetail;